import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { router } from './app/router';
import { queryClient } from './lib/query-client';
import { logger } from './shared/logging/logger';
import { handleNavigationForViewOverride } from './features/view/use-view';
import './styles/styles.scss';

// Initialize diagnostic logging before the app mounts so the earliest errors and
// requests are captured. StrictMode is intentionally OFF (see note below), so this
// runs exactly once.
logger.init();

// Log client-side navigation. React Router v7's data router exposes subscribe().
router.subscribe((state) => {
  logger.info('app', 'navigation', {
    pathname: state.location.pathname,
    search: state.location.search,
  });
  // The view override (set on a critical HTTP error / visual-search, or by
  // clearSearchBar's forceStayOnResults) is bound to the current screen — drop it as
  // soon as the user navigates anywhere, UNLESS this is the one navigation a sticky
  // override was set to survive (see handleNavigationForViewOverride).
  handleNavigationForViewOverride();
});

// NOTE(port-integration): StrictMode is intentionally NOT used. The ported
// Angular components depend on run-once mount-effect semantics — network
// bootstrap calls (MainContent's startLoadingProcess), window listeners that
// Angular never removed (App/NavBar), GSAP tween setup — exactly as Angular's
// single ngOnInit/ngAfterViewInit lifecycle ran them. StrictMode's dev-mode
// effect double-invocation would re-fire those side effects (only some are
// ref-guarded), so it is omitted to preserve exact single-run mount semantics.
// TanStack Query pilot: a single QueryClientProvider at the root makes the
// shared cache available to every useQuery in the tree. (The React Query
// Devtools floating panel is intentionally not mounted — removed for now.)
createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
  </QueryClientProvider>,
);

// The static index.html preloader (#app-preloader) is NOT removed here — it is
// the single app loading screen, faded out and removed by AppLoader once the
// data bootstrap completes. See shared/components/AppLoader.tsx.
